import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { CREATE_DEFICIENCY_FIELDS as F } from "@features/deficiencies/create/constants";
import {
  getDeficiencyErrorMessage,
  getUploadedDeficiencyDocumentUrls,
  useDeficiencyDocumentUpload,
} from "@features/deficiencies/hooks/useDeficiencyDocumentUpload";
import type { CreateDeficiencyRequestPayload } from "@services/deficiencies/types";
import { createDeficiency } from "@services/deficienciesApi";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { mapCreateDeficiencyFormToPayload } from "../mappers";
import type { CreateDeficiencyFormData } from "../schema";

const DEFICIENCY_ATTACHMENT_DOCUMENT_TYPE = "ADDITIONAL_DOCUMENT";

const getUploadedAttachmentDocuments = (attachments?: CreateDeficiencyFormData["attachments"]) => {
  const uploadEntries = attachments as UploadEntry[] | undefined;

  return getUploadedDeficiencyDocumentUrls(uploadEntries).map((documentUrl, index) => ({
    documentUrl,
    documentName: uploadEntries?.[index]?.name || "",
  }));
};

export const useCreateDeficiencyRequest = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const {
    uploadEntries: attachmentFiles,
    setUploadEntries: setAttachmentFiles,
    handleUpload: handleAttachmentUpload,
    resetUpload: resetAttachmentUpload,
    uploadedDocumentUrls: uploadedAttachmentUrls,
    isUploading: isUploadingAttachments,
  } = useDeficiencyDocumentUpload({
    storeKey: F.ATTACHMENTS,
    certificateType: DEFICIENCY_ATTACHMENT_DOCUMENT_TYPE,
    fallbackFileNamePrefix: "attachment",
    errorNotification: {
      titleKey: "deficiencies.notifications.submit-error-title",
      descriptionKey: "deficiencies.notifications.submit-error-description",
    },
  });

  const handleSubmitError = (error: unknown) => {
    notify({
      title: t("deficiencies.notifications.submit-error-title"),
      description:
        getDeficiencyErrorMessage(error) ??
        t("deficiencies.notifications.submit-error-description"),
      type: "error",
    });

    console.error("Create deficiency request failed:", error);
  };

  const createDeficiencyMutation = useMutation<void, unknown, CreateDeficiencyRequestPayload>({
    mutationFn: createDeficiency,
    onSuccess: () => {
      notify({
        title: t("deficiencies.notifications.submit-success-title"),
        description: t("deficiencies.notifications.submit-success-description"),
        type: "success",
      });
    },
    onError: handleSubmitError,
  });

  const submitRequest = async (formValues: CreateDeficiencyFormData) => {
    try {
      const documents = uploadedAttachmentUrls.length
        ? uploadedAttachmentUrls.map((documentUrl, index) => ({
            documentUrl,
            documentName: attachmentFiles[index]?.name || "",
          }))
        : getUploadedAttachmentDocuments(formValues.attachments);
      const payload = mapCreateDeficiencyFormToPayload(formValues, documents);
      await createDeficiencyMutation.mutateAsync(payload);
      return true;
    } catch {
      return false;
    }
  };

  return {
    attachmentFiles,
    setAttachmentFiles,
    handleAttachmentUpload,
    resetAttachmentUpload,
    uploadedAttachmentUrls,
    isUploadingAttachments,
    isSubmitting: createDeficiencyMutation.isPending || isUploadingAttachments,
    submitRequest,
  };
};
